import _ from 'lodash'
import { Observable } from 'rxjs';

import { MediaUploadResult, MediaUploadResult2 } from '@skinsoft/types';
import { OfflineDataUpdateCacher } from '@skinsoft/offline'
import { DEFAULT_PROXY } from '@skinsoft/network'
import { dbCacheUp } from '@skinsoft/offline/dexie/cache-up';
import { MediaTransferHelper } from './media-transfer-helper'

declare const REPORTER_SUBALBUM_ID: string
declare const API_ENDPOINT: string
declare const APP_NAME: string

declare const config
const LOGO_MIME_TYPES: string[] = config.conditionReportPDFGenerator.logoMimeTypes

export class ImageTransferHelper {

    private proxy = DEFAULT_PROXY;

    public static getInstance() {
        if (!ImageTransferHelper.instance) {
            ImageTransferHelper.instance = new ImageTransferHelper();
        }

        return ImageTransferHelper.instance;
    }

    public static install(Vue) {
        Vue.prototype.$imageTransferHelper = ImageTransferHelper.getInstance();
    }

    private static instance: ImageTransferHelper = null as any;
    private indexedDbTable = dbCacheUp.media

    public replaceImageCacheKeyWithIdFromFileName(fileName: string) {
        return MediaTransferHelper.getInstance().replaceMediaCacheKeyWithIdFromFileName(fileName, this.indexedDbTable)
    }
    
    public repeatFindingMediaIdFromFileName(fileName: string) {
        return MediaTransferHelper.getInstance().repeatFindingMediaIdFromFileName(fileName)
    }

    public repeatFindingMediaIdFromFileNameWithLimit(fileName: string, maxTry: number = 20, tryDelay: number = 500) {
        return MediaTransferHelper.getInstance().repeatFindingMediaIdFromFileNameWithLimit(fileName, maxTry, tryDelay)
    }

    public downloadImage(mediaId: string, fileType = "vignette250", offlineLoading = false) {
        return MediaTransferHelper.getInstance().downloadMedia(mediaId, fileType, offlineLoading, this.indexedDbTable)
    }

    public downloadImageOffline(mediaId: string) {
        return MediaTransferHelper.getInstance().downloadMediaOffline(mediaId, this.indexedDbTable)
    }

    public downloadOriginalImage(mediaId: string) {
        return MediaTransferHelper.getInstance().downloadOriginalMedia(mediaId)
    }

    public uploadImage(mimeType: string, file: File, localUrl: string, isAssociatedDocument?, isOffline?: boolean): MediaUploadResult {
        return MediaTransferHelper.getInstance().uploadMedia(mimeType, file, localUrl, this.indexedDbTable, isAssociatedDocument, isOffline)
    }

    public uploadImageInCache(file: File, localUrl: string) {
        return MediaTransferHelper.getInstance().uploadMediaInCache(file, localUrl, this.indexedDbTable)
    }

    public uploadLocalImageToApi(cacheKey: string, useCacheKeyAsFileName: boolean = false): Observable<MediaUploadResult2> {
        return MediaTransferHelper.getInstance().uploadLocalMediaToApi(cacheKey, useCacheKeyAsFileName, this.indexedDbTable)
    }

    public deleteImageInCache(id: string) {
        this.indexedDbTable.delete(id)
    }

    public async cleanCachedImages() {
        const cachedMediakeys = await this.indexedDbTable.toCollection().primaryKeys()
        const cachedEntities = await OfflineDataUpdateCacher.getAllEntities()
        
        // if the image have been uploaded, there is no need to keep them in cache

        const cachedEntitiesJSON = cachedEntities.map(async (entity) => {
            return JSON.stringify(entity)
        })

        // for the local images, we need to check that they are not used in a cached entity
        for(const key of cachedMediakeys) {
            // if the media has been added offline we have to check that it is not referenced in a cached entity
            // else we can delete it directly because it will be fetched from the api anyway
            if(MediaTransferHelper.isLocalMedia(key)) {
                let canDelete = true
                for(const cachedEntityJSON of cachedEntitiesJSON) {
                    if((await cachedEntityJSON).includes(key)) {
                        canDelete = false
                        break
                    }
                }
                if(!canDelete) {
                    continue
                }
            }
            this.indexedDbTable.delete(key)
        }
    }

    public async getCachedImagesKeys(): Promise<string[]> {
        return await this.indexedDbTable.toCollection().primaryKeys()
    }

    public addImageToReporterSubAlbum(idImage) {
        return this.addImageToAlbum(REPORTER_SUBALBUM_ID, idImage)
    }

    public addImageToAlbum(idAlbum: string, idImage: string) {
        return this.proxy.fetch(`/albums/addMedia/${idAlbum}?idMedia=${idImage}`, undefined, 'PUT')
    }
    
    public getCachedMediaValue(cacheKey: string): Observable<string | File> {
        return MediaTransferHelper.getInstance().getCachedMediaValue(cacheKey, this.indexedDbTable)
    }

    public getCachedMediaValueAsBase64(cacheKey: string): Observable<string> {
        return MediaTransferHelper.getInstance().getCachedMediaValueAsBase64(cacheKey, this.indexedDbTable)
    }
}